import { Box } from '@lifeomic/chroma-react/components/Box';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ChevronRight } from '@lifeomic/chromicons';
import { lighten } from '@mui/material/styles';
import clsx from 'clsx';
import { Text } from '@lifeomic/chroma-react/components/Text';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FixedSizeList } from 'react-window';
import { MedicalResultChartCondensed } from '../Measurements/Graphs/Ranges/MedicalResultsChartCondensed/MedicalResultChartCondensed';
import { NoDataGuard } from './NoDataGuard';
import { useFetchDimensionData } from './hooks/useFetchDimensionData';
import {
  MedicalResultHistory,
  useFetchMedicalResultHistories,
} from './hooks/useFetchMedicalResultHistories';
import { useSearchContext } from './providers/Search';
import { useSelectionContext } from './providers/Selection';
import { ReviewCodeMappingAction } from './ReviewCodeMappingAction';
import { LabResultsConfigItem } from './useLabResultsConfig';
import { filterMedicalResultHistories } from './utils';

const ROW_HEIGHT = 185;

export const useStyles = makeStyles(theme => ({
  titleContainer: {
    alignItems: 'center',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : lighten(theme.palette.background.default, 0.5),
    color: theme.palette.text.primary,
    display: 'flex',
    fontSize: theme.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    gap: theme.spacing(0.75),
    letterSpacing: theme.pxToRem(1.2),
    padding: theme.spacing(1, 2),
    position: 'sticky',
    textTransform: 'uppercase',
    zIndex: 30,
    top: 0,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.action.hover
          : lighten(theme.palette.background.default, 0.3),
    },
  },
  titleContainerExpanded: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  row: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  btn: {
    position: 'relative',
    maxHeight: '100%',
  },
  sectionContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  section: {
    '&:hover, &:focus': {
      backgroundColor: theme.palette.action.hover,
      transition: 'background 0.25s ease-out',
      '& $chevron': {
        transform: `translate3d(${theme.spacing(1)}, 0, 0)`,
        transition: 'transform 0.125s ease',
      },
    },
  },
  chevronContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    minWidth: theme.spacing(12),
    paddingRight: theme.spacing(1),
  },
  chevron: {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.text.secondary
        : theme.palette.black[500],
    transition: 'transform 0.125s ease',
    transform: 'rotate(0deg)',
  },
  chevronExpanded: {
    transform: 'rotate(90deg)',
  },
  graphContainer: {
    maxHeight: ROW_HEIGHT,
  },
  paddingTop: {
    paddingTop: theme.spacing(3),
  },
  emptyState: {
    color: theme.palette.text.secondary,
  },
}));

type LabResultsItemRowProps = {
  medicalResultHistory: MedicalResultHistory;
  patientId?: string;
  showReviewCodingAction?: boolean;
  onOpenSourceDocument?: (
    document: fhir.DocumentReference,
    history: MedicalResultHistory,
    resourceId?: string,
  ) => void;
};

const LabResultsItemRow = React.memo(
  ({
    medicalResultHistory,
    patientId,
    showReviewCodingAction,
    onOpenSourceDocument,
  }: LabResultsItemRowProps) => {
    const classes = useStyles();

    return (
      <NoDataGuard
        dimensionDatum={medicalResultHistory}
        results={medicalResultHistory.medicalResultHistory}
      >
        <Box
          className={classes.section}
          align="center"
          justify="space-between"
          fullWidth
        >
          <Box className={classes.graphContainer} fullWidth>
            <MedicalResultChartCondensed.WithPrefetchedData
              resultsHistory={medicalResultHistory}
              contained
              subjectId={patientId}
              onOpenSourceDocument={(document, resourceId) =>
                onOpenSourceDocument?.(
                  document,
                  medicalResultHistory,
                  resourceId,
                )
              }
            />
          </Box>
          <Box className={classes.chevronContainer}>
            {showReviewCodingAction ? (
              <ReviewCodeMappingAction
                medicalResultHistory={medicalResultHistory}
              />
            ) : null}
            <ChevronRight className={classes.chevron} width={18} height={18} />
          </Box>
        </Box>
      </NoDataGuard>
    );
  },
);

const messages = defineMessages({
  noData: {
    id: 'labResults.item.noData',
    defaultMessage: 'No Results',
  },
});

type LabResultsItemProps = {
  patient: fhir.Patient;
  labResultsConfigItem: LabResultsConfigItem;
  onClick: (
    medicalResultHistory: MedicalResultHistory,
    options?: { preferPrefetchedMedicalResults?: boolean },
  ) => void;
  onOpenSourceDocument?: (
    document: fhir.DocumentReference,
    history: MedicalResultHistory,
    resourceId?: string,
  ) => void;
};

export function LabResultsItem({
  patient,
  labResultsConfigItem,
  onClick,
  onOpenSourceDocument,
}: LabResultsItemProps) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const labResultSectionRef = useRef<HTMLDivElement>(null);
  const { debouncedSearchTerm, recency, annotatedOnly, extractedOnly } =
    useSearchContext();
  const { selection } = useSelectionContext();

  const { formatMessage } = useIntl();
  const showReviewCodingAction = labResultsConfigItem.source === 'unmappedLabs';

  const { data: dimensionData } = useFetchDimensionData({
    patientId: patient.id as string,
    labResultsConfigItem,
  });

  const { data: medicalResultHistories, isLoading } =
    useFetchMedicalResultHistories({
      patientId: patient.id as string,
      dimensionData,
      labResultsConfigItem,
    });

  const filteredMedicalResultHistories = useMemo(() => {
    if (medicalResultHistories === undefined) {
      return [];
    }
    return filterMedicalResultHistories(
      medicalResultHistories,
      debouncedSearchTerm,
      recency,
      annotatedOnly,
      extractedOnly,
    );
  }, [
    medicalResultHistories,
    debouncedSearchTerm,
    recency,
    annotatedOnly,
    extractedOnly,
  ]);

  const scrollToLabResultSection = useCallback(() => {
    if (labResultSectionRef.current) {
      labResultSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (selection?.title === labResultsConfigItem.title) {
      scrollToLabResultSection();
    }
  }, [selection, scrollToLabResultSection, labResultsConfigItem.title]);

  return (
    <Box
      direction="column"
      className={classes.sectionContainer}
      fullWidth
      ref={labResultSectionRef}
    >
      <Box
        className={`${classes.titleContainer} ${
          isExpanded || debouncedSearchTerm || extractedOnly
            ? classes.titleContainerExpanded
            : ''
        }`}
        fullWidth
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ChevronRight
          className={`${classes.chevron} ${
            isExpanded ||
            // if a filter has results, show the chevron expanded
            ((debouncedSearchTerm || extractedOnly) &&
              filteredMedicalResultHistories.length > 0)
              ? classes.chevronExpanded
              : ''
          }`}
          width={18}
          height={18}
        />
        {labResultsConfigItem.title}
      </Box>
      {(isExpanded || debouncedSearchTerm || extractedOnly) &&
        (isLoading ? (
          <Box fullWidth fullHeight justify="center" align="center" width={15}>
            <DotLoader size={0} />
          </Box>
        ) : filteredMedicalResultHistories.length === 0 && isExpanded ? (
          // if it's explicitly expanded and there are no results, show the no data message
          <Box padding={2} direction="column" fullWidth>
            <Text className={classes.emptyState} size="subbody">
              {formatMessage(messages.noData)}
            </Text>
          </Box>
        ) : filteredMedicalResultHistories.length < 30 ? (
          // if there are fewer than 30 results, show them all for better scroll behavior
          filteredMedicalResultHistories.map((history, index) => (
            <>
              {index > 0 && <Divider spacing={3} />}
              <ButtonUnstyled
                className={clsx(classes.btn, {
                  [classes.paddingTop]: index === 0,
                })}
                key={history.dimension.id}
                onClick={() =>
                  onClick(history, {
                    preferPrefetchedMedicalResults: extractedOnly,
                  })
                }
                fullWidth
              >
                <LabResultsItemRow
                  medicalResultHistory={history}
                  patientId={patient.id}
                  showReviewCodingAction={showReviewCodingAction}
                  onOpenSourceDocument={onOpenSourceDocument}
                />
              </ButtonUnstyled>
            </>
          ))
        ) : (
          <FixedSizeList
            itemSize={ROW_HEIGHT}
            itemCount={filteredMedicalResultHistories.length}
            height={Math.min(
              filteredMedicalResultHistories.length * ROW_HEIGHT,
              500,
            )}
            width={'100%'}
            overscanCount={5} // render up to 5 extra items to avoid flickering
          >
            {({ index, style }) => (
              <div style={style} className={classes.row}>
                <ButtonUnstyled
                  className={classes.btn}
                  key={filteredMedicalResultHistories[index].dimension.id}
                  onClick={() => {
                    onClick(filteredMedicalResultHistories[index], {
                      preferPrefetchedMedicalResults: extractedOnly,
                    });
                  }}
                  fullWidth
                >
                  <LabResultsItemRow
                    medicalResultHistory={filteredMedicalResultHistories[index]}
                    patientId={patient.id}
                    showReviewCodingAction={showReviewCodingAction}
                    onOpenSourceDocument={onOpenSourceDocument}
                  />
                </ButtonUnstyled>
              </div>
            )}
          </FixedSizeList>
        ))}
    </Box>
  );
}
